// RunDetailDialog — D3: expandera en run för per-step-output + error-
// details. Klick på en run-row i RunHistoryPanel öppnar denna dialog.
//
// Queries run_steps via supabase-client (RLS-gate:d till owner/coop-
// editor/sysadmin). Ingen server-side-RPC behövs — tabellen har
// redan rätt RLS-policies från P-001.

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, CheckCircle2, XCircle, SkipForward, Clock, Loader } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type StepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

interface StepRow {
  id: string;
  block_id: string;
  sequence: number;
  status: StepStatus;
  started_at: string | null;
  ended_at: string | null;
  input: unknown;
  output: unknown;
  error_message: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string | null;
}

const STATUS_STYLES: Record<StepStatus, { variant: 'default' | 'destructive' | 'secondary' | 'outline'; icon: typeof CheckCircle2 }> = {
  succeeded: { variant: 'default', icon: CheckCircle2 },
  failed: { variant: 'destructive', icon: XCircle },
  running: { variant: 'secondary', icon: Loader },
  pending: { variant: 'outline', icon: Clock },
  skipped: { variant: 'outline', icon: SkipForward },
};

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '—';
  try { return JSON.stringify(value, null, 2); }
  catch { return String(value); }
}

export function RunDetailDialog({ open, onOpenChange, runId }: Props) {
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !runId) return;
    (async () => {
      setIsLoading(true);
      setError(null);
      // A-067: tidigare cast:ades hela `supabase`-klienten via
      // `as unknown as { from: ... }` eftersom Database-typerna inte
      // var regenererade när P-001-tabellen `run_steps` landade.
      // Typerna är nu i `src/integrations/supabase/types.ts:969`, så
      // wrapper-cast:en är borttagen. `status`-fältet i Database-typen
      // är `string` (DB-spec) men `StepRow` här begränsar till en
      // 5-värdes-enum — narrow:as via `as StepRow[]` vid set-tid där
      // alternativet är runtime-validering som sker via badge-rendering
      // (okänt status → outline-fallback).
      const { data, error: err } = await supabase
        .from('run_steps')
        .select('id, block_id, sequence, status, started_at, ended_at, input, output, error_message')
        .eq('run_id', runId)
        .order('sequence', { ascending: true });
      if (err) {
        setError(err.message);
        setSteps([]);
      } else {
        setSteps((data ?? []) as StepRow[]);
      }
      setIsLoading(false);
    })();
  }, [open, runId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 gap-0" data-testid="run-detail-dialog">
        <DialogHeader className="px-5 py-3 border-b flex-shrink-0">
          <DialogTitle>Run-detaljer</DialogTitle>
          <DialogDescription className="text-xs font-mono">
            {runId ?? 'Ingen run vald'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {isLoading && (
              <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Laddar steps…
              </div>
            )}
            {error && (
              <div className="p-3 text-xs text-destructive bg-destructive/10 rounded-md">
                Kunde inte ladda steps: {error}
              </div>
            )}
            {!isLoading && !error && steps.length === 0 && (
              <div className="text-center py-8 text-xs text-muted-foreground">
                Inga steps registrerade för denna run.
              </div>
            )}
            {steps.map((step) => {
              const style = STATUS_STYLES[step.status];
              const StatusIcon = style.icon;
              return (
                <div
                  key={step.id}
                  className="border rounded-md p-3 space-y-2"
                  data-testid={`step-${step.sequence}`}
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono font-semibold text-muted-foreground">
                      #{step.sequence}
                    </span>
                    <Badge variant={style.variant} className="gap-1">
                      <StatusIcon className={`w-3 h-3 ${step.status === 'running' ? 'animate-spin' : ''}`} />
                      {step.status}
                    </Badge>
                    <span className="font-mono text-[11px] flex-1 truncate">
                      {step.block_id}
                    </span>
                    <span className="text-muted-foreground text-[10px] tabular-nums">
                      {formatDuration(step.started_at, step.ended_at)}
                    </span>
                  </div>

                  {step.error_message && (
                    <div className="p-2 bg-destructive/10 border border-destructive/30 rounded text-[11px] text-destructive">
                      <strong>Error:</strong> {step.error_message}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <div className="font-medium text-muted-foreground mb-1">Input</div>
                      <pre className="bg-muted/50 p-2 rounded overflow-auto max-h-40 font-mono text-[10px]">
                        {formatJson(step.input)}
                      </pre>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground mb-1">Output</div>
                      <pre className="bg-muted/50 p-2 rounded overflow-auto max-h-40 font-mono text-[10px]">
                        {formatJson(step.output)}
                      </pre>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
